import { SalesPerformanceApi } from "@services/sales-performance/sales-performance.api";
import { format } from "date-fns";

export type Checks = {
  sales: boolean;
  spend: boolean;
  impressions: boolean;
  ctr: boolean;
  cvr: boolean;
  cpc: boolean;
  acos: boolean;
  roas: boolean;
  orders: boolean;
  unitSolds: boolean;
  clicks: boolean;
  tacos: boolean;
  organicRank: boolean;
  searchTermAdImpressionRank: boolean;
  timeInBudget: boolean;
  tosAdImpressionShare: boolean;
};

export const lineStyle = {
  strokeWidth: 2,
  dot: false,
  activeDot: false,
  xAxisId: "lineAxis",
};

export const areaStyle = {
  strokeWidth: 2,
  strokeDasharray: "3 3",
};

export const getInterval = (
  length: number,
  isMobile: boolean,
): number | "preserveStartEnd" => {
  const rules: Array<[number, number | "preserveStartEnd"]> = isMobile
    ? [
        [8, 0],
        [31, 1],
        [90, 3],
        [Infinity, "preserveStartEnd"],
      ]
    : [
        [8, 0],
        [31, 1],
        [90, 6],
        [182, 13],
        [365, 29],
        [Infinity, 30],
      ];
  return rules.find(([max]) => length <= max)![1];
};

export const getTickProps = (length: number) => {
  if (length > 20) {
    return { angle: -45, textAnchor: "end" };
  }
  return {
    fill: "#FFFFFF",
    fontSize: 12,
  };
};

export const formatThousands: (tick: number) => string = (tick) => {
  if (tick < 1000) return tick.toString();
  return `${(tick / 1000).toFixed(1)}k`;
};

export const getData = async (
  dateRange: { startDate: Date; endDate: Date },
  checks: Partial<Checks>,
) => {
  const { startDate, endDate } = dateRange;
  const startDateString = startDate.toISOString().split("T")[0];
  const endDateString = endDate.toISOString().split("T")[0];

  const salesPerformanceApi = new SalesPerformanceApi();

  const data: any = {
    sales: null,
    spend: null,
    impressions: null,
    ctr: null,
    cvr: null,
    cpc: null,
    acos: null,
    roas: null,
    unitSolds: null,
    clicks: null,
  };

  const promises: Promise<any>[] = [];
  const keys: string[] = [];

  for (const key of Object.keys(checks)) {
    if (checks[key as keyof Checks]) {
      let promise: Promise<any> | null = null;
      switch (key) {
        case "sales":
          promise = salesPerformanceApi.getTotalSales(
            startDateString,
            endDateString,
          );
          break;
        case "spend":
          promise = salesPerformanceApi.getAdSpend(
            startDateString,
            endDateString,
          );
          break;
        case "impressions":
          promise = salesPerformanceApi.getAdImpressions(
            startDateString,
            endDateString,
          );
          break;
        case "ctr":
          promise = salesPerformanceApi.getAdCtr(
            startDateString,
            endDateString,
          );
          break;
        case "cvr":
          promise = salesPerformanceApi.getAdCvr(
            startDateString,
            endDateString,
          );
          break;
        case "cpc":
          promise = salesPerformanceApi.getAdCpc(
            startDateString,
            endDateString,
          );
          break;
        case "acos":
          promise = salesPerformanceApi.getAdAcos(
            startDateString,
            endDateString,
          );
          break;
        case "roas":
          promise = salesPerformanceApi.getAdRoas(
            startDateString,
            endDateString,
          );
          break;
        case "unitSolds":
          promise = salesPerformanceApi.getAdUnitsSold(
            startDateString,
            endDateString,
          );
          break;
        case "clicks":
          promise = salesPerformanceApi.getAdClicks(
            startDateString,
            endDateString,
          );
          break;
        default:
          break;
      }
      if (promise !== null) {
        promises.push(promise);
        keys.push(key);
      }
    }
  }

  const results = await Promise.all(promises);

  keys.forEach((key, index) => {
    data[key] = results[index];
  });

  return {
    date: generateDays(startDate, endDate),
    ...data,
  };
};

export const generateDays = (startDate: Date, endDate: Date): string[] => {
  const days =
    (endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24) + 1;
  const data: string[] = [];
  for (let i = 0; i < days; i++) {
    const currentDate = new Date(startDate);
    currentDate.setDate(startDate.getDate() + i);
    data.push(format(currentDate, "d MMM"));
  }
  return data;
};

export const transformData = (rawData: any) => {
  return rawData.date.map((date: string, index: number) => ({
    date,
    isForecast: false,
    isPastData: false,
    sales: rawData.sales[index] ?? 0,
    spend: rawData.spend[index] ?? 0,
    impressions: rawData.impressions[index] ?? 0,
    ctr: Number(rawData.ctr[index]) * 100 || 0,
    cvr: Number(rawData.cvr[index]) * 100 || 0,
    cpc: rawData.cpc[index] ?? 0,
    acos: Number(rawData.acos[index]) * 100 || 0,
    roas: Number(rawData.roas[index]) || 0,
    unitSolds: rawData.unitSolds[index] ?? 0,
  }));
};

const metrics = [
  "sales",
  "spend",
  "impressions",
  "ctr",
  "cvr",
  "cpc",
  "acos",
  "roas",
  "unitSolds",
] as const;

function capitalize(str: string): string {
  return str.charAt(0).toUpperCase() + str.slice(1);
}

export const generatePastData = (
  data: any[],
  numDays: number,
  firstDataPoint: any,
  startDate: Date,
): any[] => {
  const result = data
    .map((current, index) => {
      if (!current) return null;

      const isPast = index < numDays;
      const item: any = { ...current, isPastData: isPast };

      if (isPast) {
        const date = new Date(startDate);
        date.setDate(date.getDate() + index);
        item.date = date;

        metrics.forEach((key) => {
          const value = current[key];
          if (value !== undefined) {
            item[`past${capitalize(key)}`] = generatePastValue(value);
          }
        });
      }

      return item;
    })
    .filter((x): x is any => x !== null);

  result.push({ ...firstDataPoint, isPastData: true });

  return result;
};

export const generatePastValue = (index: number): number =>
  Math.random() * 10 * (1 + 0.02 * (index + 1));

export const generateForecastData = (
  numDays: number,
  lastDataPoint: any,
  startDate: string,
): any[] => {
  const dates = Array.from({ length: numDays }, (_, i) => {
    const d = new Date(startDate);
    d.setDate(d.getDate() + i);
    return format(d, "d MMM");
  });

  const metrics = [
    "sales",
    "spend",
    "impressions",
    "ctr",
    "cvr",
    "cpc",
    "acos",
    "roas",
    "unitSolds",
  ] as const;

  return dates.map((date, index) => {
    const obj: any = {
      date,
      isForecast: true,
      isPastData: false,
    };
    metrics.forEach((metric) => {
      const base = lastDataPoint[metric];
      if (base !== undefined) {
        obj[metric] = generateForecastValue(base, index);
      }
    });

    return obj;
  });
};

const generateForecastValue = (lastValue: number, index: number): number => {
  if (index === 0) {
    return lastValue;
  }
  return Math.random() * 10 * (1 + 0.02 * (index + 1));
};
